import { useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { ActionInbox } from "@/components/shared/ActionInbox";
import { MyDayMetricsStrip } from "@/components/shared/MyDayMetricsStrip";
import { Loading } from "@/components/shared/Loading";
import { AutoFileDialog } from "@/components/shared/AutoFileDialog";
import { Button } from "@/components/ui/button";
import { useActions } from "@/api/hooks";
import { UploadCloud } from "lucide-react";

export default function OpsMyDay() {
  const [uploadOpen, setUploadOpen] = useState(false);
  const { data: actions, isLoading } = useActions("operations");
  if (isLoading || !actions) return <Loading />;
  const open = actions.filter((action) => !action.done);
  const urgent = open.filter((action) => action.priority === "P1").length;

  // Item 5/13 — reuses the SAME server-computed My Day tags (New Handoff /
  // Booking Required / Cutoff / Free-Time Risk) already emitted by
  // GET /actions/operations (server.py) rather than re-deriving eligibility
  // client-side.
  const itemsByTags = (...tags) => open.filter((a) => tags.includes(a.tag));

  // Item 1 (Aug 2026 round) — a KPI tile must open ONLY the specific
  // job/customer it represents, never a generic "All Jobs" list. Each tag's
  // matching action items already carry a real `link` (job or
  // operations_handoff) — the exact same link ActionInbox below uses to
  // route individual rows. Reuse that:
  //  - 0 matches -> the tile is inert (no navigation at all).
  //  - exactly 1 distinct linked record -> jump straight to that job/handoff.
  //  - >1 -> there is no single correct target, so scroll down to the
  //    already-tag-filtered rows in the Action Inbox below instead of
  //    inventing/opening an unrelated unfiltered list route.
  const routeForLink = (link) => {
    if (!link?.type || !link?.id) return null;
    if (link.type === "job") return `/operations/jobs/${link.id}`;
    if (link.type === "operations_handoff") return `/operations/handoffs/${link.id}`;
    return null;
  };
  const targetFor = (...tags) => {
    const matches = itemsByTags(...tags);
    if (!matches.length) return { count: 0 };
    const distinctLinks = [...new Map(matches.filter((m) => m.link?.id).map((m) => [`${m.link.type}:${m.link.id}`, m.link])).values()];
    if (distinctLinks.length === 1) return { count: matches.length, route: routeForLink(distinctLinks[0]) };
    return { count: matches.length, route: null };
  };
  const scrollToInbox = () => document.getElementById("ops-action-inbox")?.scrollIntoView({ behavior: "smooth", block: "start" });
  const clickFor = (target) => (nav) => {
    if (!target.count) return; // nothing to open
    if (target.route) nav(target.route);
    else scrollToInbox();
  };

  const newHandoffsTarget = targetFor("New Handoff");
  const bookingsPendingTarget = targetFor("Booking Required", "New Job to Plan");
  const cutoffsApproachingTarget = targetFor("Cutoff");
  const freeTimeRiskTarget = targetFor("Free-Time Risk");
  const metrics = [
    { key: "new-handoffs", label: "New Handoffs", value: newHandoffsTarget.count, onClick: clickFor(newHandoffsTarget), tone: newHandoffsTarget.count ? "warn" : "default" },
    { key: "bookings-pending", label: "Bookings Pending", value: bookingsPendingTarget.count, onClick: clickFor(bookingsPendingTarget) },
    { key: "cutoffs-approaching", label: "Cutoffs Approaching", value: cutoffsApproachingTarget.count, onClick: clickFor(cutoffsApproachingTarget), tone: cutoffsApproachingTarget.count ? "warn" : "default" },
    { key: "free-time-risk", label: "Free-Time Risk", value: freeTimeRiskTarget.count, onClick: clickFor(freeTimeRiskTarget), tone: freeTimeRiskTarget.count ? "danger" : "default" },
  ];

  return (
    <>
      <PageHeader
        title="Operations workspace"
        subtitle={`${open.length} open operational ${open.length === 1 ? "action" : "actions"}${urgent ? ` · ${urgent} urgent` : ""}`}
        actions={(
          <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={() => setUploadOpen(true)} data-testid="ops-upload-btn">
            <UploadCloud className="mr-1.5 h-3.5 w-3.5" />Upload document
          </Button>
        )}
      />
      <div className="flex-1 overflow-y-auto">
        <MyDayMetricsStrip items={metrics} />
        <div className="px-4 pt-5 md:px-6">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Queue summary</div>
            <div className="mt-1 text-sm text-slate-700">
              {open.length > 0
                ? `${open.length} open ${open.length === 1 ? "item requires" : "items require"} operational review${urgent ? `, including ${urgent} urgent` : ""}.`
                : "There are no open operational actions."}
            </div>
          </div>
        </div>
        <div id="ops-action-inbox" className="px-4 pb-1.5 pt-5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 md:px-6">Action inbox</div>
        <div className="mx-4 mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white md:mx-6"><ActionInbox items={actions} /></div>
      </div>
      <AutoFileDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </>
  );
}
